export interface BufferedMessage {
  documentId: string;
  text: string;
  isFinal: boolean;
  bufferedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

export type BufferReleaseCallback = (
  documentId: string,
  text: string,
  isFinal: boolean
) => void;

/**
 * Holds messages for a configurable delay before releasing them.
 * Allows translations to stabilize before the typing animation begins.
 */
export class MessageBuffer {
  private pending = new Map<string, BufferedMessage>();
  private bufferMs: number;
  private onRelease: BufferReleaseCallback;

  constructor(bufferMs: number, onRelease: BufferReleaseCallback) {
    this.bufferMs = bufferMs;
    this.onRelease = onRelease;
  }

  /**
   * Add or update a message in the buffer.
   * New messages start a timer based on messageTimestamp (or now).
   * Existing messages update text in place, keeping the original timer.
   */
  add(
    documentId: string,
    text: string,
    isFinal: boolean,
    messageTimestamp?: number
  ): void {
    const existing = this.pending.get(documentId);
    const now = Date.now();

    if (existing) {
      existing.text = text;
      existing.isFinal = isFinal;
      if (isFinal && now - existing.bufferedAt >= this.bufferMs) {
        this.release(documentId);
      }
      return;
    }

    const effectiveStartTime = messageTimestamp ?? now;
    const remainingMs = Math.max(0, this.bufferMs - (now - effectiveStartTime));

    const buffered: BufferedMessage = {
      documentId,
      text,
      isFinal,
      bufferedAt: effectiveStartTime,
      timer: null,
    };

    if (remainingMs <= 0) {
      this.pending.set(documentId, buffered);
      this.release(documentId);
      return;
    }

    buffered.timer = setTimeout(() => this.release(documentId), remainingMs);
    this.pending.set(documentId, buffered);
  }

  remove(documentId: string): void {
    const buffered = this.pending.get(documentId);
    if (buffered) {
      if (buffered.timer) clearTimeout(buffered.timer);
      this.pending.delete(documentId);
    }
  }

  isBuffered(documentId: string): boolean {
    return this.pending.has(documentId);
  }

  getRemainingTime(documentId: string): number {
    const buffered = this.pending.get(documentId);
    if (!buffered) return 0;
    return Math.max(0, this.bufferMs - (Date.now() - buffered.bufferedAt));
  }

  get count(): number {
    return this.pending.size;
  }

  setBufferMs(ms: number): void {
    this.bufferMs = ms;

    // When buffer is reduced, release any pending messages whose time has elapsed
    if (this.pending.size > 0) {
      const now = Date.now();
      for (const [docId, buffered] of [...this.pending]) {
        const remaining = ms - (now - buffered.bufferedAt);
        if (remaining <= 0) {
          if (buffered.timer) {
            clearTimeout(buffered.timer);
            buffered.timer = null;
          }
          this.release(docId);
        }
      }
    }
  }

  /** Releases all buffered messages immediately, calling onRelease for each. */
  releaseAll(): void {
    const docIds = [...this.pending.keys()];
    for (const docId of docIds) {
      this.release(docId);
    }
  }

  destroy(): void {
    for (const [, buffered] of this.pending) {
      if (buffered.timer) clearTimeout(buffered.timer);
    }
    this.pending.clear();
  }

  private release(documentId: string): void {
    const buffered = this.pending.get(documentId);
    if (!buffered) return;
    if (buffered.timer) clearTimeout(buffered.timer);
    this.pending.delete(documentId);
    this.onRelease(documentId, buffered.text, buffered.isFinal);
  }

  toDebugObject(): object {
    const messages: object[] = [];
    for (const [docId, buffered] of this.pending) {
      messages.push({
        documentId: docId,
        text:
          buffered.text.substring(0, 50) +
          (buffered.text.length > 50 ? '...' : ''),
        isFinal: buffered.isFinal,
        remainingMs: this.getRemainingTime(docId),
      });
    }
    return { bufferMs: this.bufferMs, count: this.pending.size, messages };
  }
}
